import { writable, get } from 'svelte/store';
import { apiClient } from '$lib/api/client';

/**
 * One technology a search can filter on.
 */
export interface Technology {
  slug: string;
  label: string;

  /**
   * What selecting this gets you, for the (i) beside the label.
   *
   * It answers "what am I selecting", never "how did you find it". Absent on
   * the signals whose label already says it, and the icon is then not drawn —
   * an (i) that explains nothing is worse than none.
   */
  means?: string;
}

/**
 * One group of them, as the panel shows it.
 *
 * The grouping is not decoration. Several tools chosen inside a group mean
 * "any of them" and several groups mean "all of them", so the panel needs the
 * lines to explain what a selection will do before the customer runs it.
 */
export interface TechnologyGroup {
  key: string;
  label: string;
  technologies: Technology[];
}

/**
 * One weakness in a company's mail authentication.
 *
 * `why` comes from the server and is shown rather than summarised. These are
 * the numbers most likely to be quoted at a customer, and "no DKIM at the
 * selectors we tried" becomes "no DKIM" in a sales conversation unless the
 * caveat travels with the filter.
 */
export interface MailGap {
  slug: string;
  label: string;
  why: string;

  /**
   * The setting this is a state of, if it is one.
   *
   * A domain has exactly one DMARC posture and one SPF posture, so those
   * become a dropdown each rather than a row of checkboxes that invites
   * ticking two answers to the same question. A gap with no group is its own
   * question and gets a checkbox.
   */
  group?: string;
}

export interface Catalogue {
  groups: TechnologyGroup[];
  mail_gaps: MailGap[];
}

export const catalogue = writable<Catalogue | null>(null);
export const catalogueFailed = writable<boolean>(false);

let inFlight: Promise<void> | null = null;

/**
 * Fetches the vocabulary once per page load.
 *
 * # Why it is not bundled with the app
 *
 * It is 244 entries in eleven groups and it changed five times in the week it
 * was written. A copy here would drift from the one the query understands, and
 * drift is invisible: the server drops a slug it does not recognise, so the
 * customer ticks a box, the count does not move, and nothing reports a fault.
 *
 * The guard is the promise rather than a loaded flag, because the panel and the
 * assistant both mount this component and would otherwise each start a request
 * before either had finished.
 */
export async function loadCatalogue(): Promise<void> {
  if (get(catalogue) !== null) return;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await apiClient.get<Catalogue>('/filters/technologies');
      catalogue.set(response.data);
      catalogueFailed.set(false);
    } catch {
      // Left null and flagged. The component shows nothing rather than an
      // empty list: an empty list reads as "no technologies to filter on",
      // which is a different and wrong statement.
      catalogueFailed.set(true);
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/*
 * In development, forget the catalogue whenever anything hot-reloads.
 *
 * `catalogue` is module state and HMR replaces components without resetting it,
 * so the guard in loadCatalogue keeps returning the copy fetched when the page
 * first loaded. Editing the vocabulary and restarting the API then changes
 * nothing on screen: the panel goes on showing the labels, the explanations and
 * the gap texts from before the restart, and the only clue is that it looks
 * exactly like a bug in whatever was just edited.
 *
 * That cost an hour of looking for a missing tooltip that was being served
 * correctly the whole time.
 *
 * Production needs none of this. The vocabulary changes when the API is
 * deployed, and the app is reloaded when it is — the guard is right there, and
 * fetching once per page load is the point of it.
 */
if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', () => {
    catalogue.set(null);
    catalogueFailed.set(false);
  });
}
